import { FormControl, FormHelperText, FormLabel, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useField } from 'formik';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@sophocles/app';

const DayOfWeekFlag = {
  MONDAY: 0,
  TUESDAY: 1,
  WEDNESDAY: 2,
  THURSDAY: 3,
  FRIDAY: 4,
  SATURDAY: 5,
  SUNDAY: 6,
};

function DayOfWeekField({
  label = null,
  required = false,
  variant = 'contained',
  size = 'normal',
  fullWidth = false,
  color = 'primary',
  disabled = false,
  error = false,
  ...props
}) {
  const [field, meta, helpers] = useField(props);

  const { t } = useTranslation();

  const { user } = useUser();
  const startOfWeek = user.get('options', Map()).get('start_of_week', 'monday');

  // Rearrange days based on the start of the week
  const days = Object.keys(DayOfWeekFlag);
  const orderedDays = startOfWeek === 'sunday' ? ['SUNDAY', ...days.slice(0, 6)] : days; // Default to Monday start if not 'sunday'

  const handleToggle = (event, newSelectedDay) => {
    // Update Formik's form state with the new selected day
    helpers.setValue(newSelectedDay);
    // Mark the field as touched
    helpers.setTouched(true);
  };

  return (
    <FormControl required={required} error={meta.touched && Boolean(meta.error)} fullWidth={fullWidth}>
      {label && <FormLabel>{label ?? 'Days of Week'}</FormLabel>}
      <ToggleButtonGroup
        name={field.name}
        variant={variant}
        color={color}
        fullWidth={fullWidth}
        size={size}
        value={field.value}
        disabled={disabled}
        onChange={handleToggle}
      >
        {orderedDays.map(key => {
          const dayKey = `general.${key.toLowerCase()}`; // Convert key to translation key (e.g., 'general.monday')
          return (
            <ToggleButton key={key} value={DayOfWeekFlag[key]} sx={{ width: '100%' }}>
              <span>{t(dayKey).substring(0, 3).toUpperCase()}</span>
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
      {meta.touched && meta.error && <FormHelperText>{meta.error}</FormHelperText>}
    </FormControl>
  );
}

DayOfWeekField.propTypes = {
  required: PropTypes.bool,
  variant: PropTypes.string,
  size: PropTypes.oneOf(['small', 'normal', 'large']),
  fullWidth: PropTypes.bool,
  helperText: PropTypes.string,
  color: PropTypes.string,
};

export default DayOfWeekField;
